import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ShieldCheck, Users, CreditCard, Bike, Home, Sparkles } from 'lucide-react';
import { Section } from './ui/Section';
export function ThreePillars() {
  const ref = useRef(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 1, 1, 0]);
  const pillars = [{
    icon: ShieldCheck,
    title: 'Physical Verification',
    description: 'Every hostel is physically inspected by our Simba Fleet ambassadors on branded bicycles before listing.',
    color: 'from-emerald-500 to-green-600',
    image: 'https://cdn.magicpatterns.com/uploads/7LEqoyU1fPppK4wpzCzf8S/Copy_of_Billvast_Shirt_Mock-up_(1).png',
    stats: '850+ Verified',
    delay: 0
  }, {
    icon: Users,
    title: 'AI Roommate Matching',
    description: 'Our smart algorithm pairs you with compatible roommates based on lifestyle, habits, and preferences.',
    color: 'from-brand-primary to-purple-600',
    image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    stats: '5,000+ Matches',
    delay: 0.2
  }, {
    icon: CreditCard,
    title: 'Secure Escrow Payments',
    description: 'Your money is held safely in escrow until you verify the property and move in. Zero fraud risk.',
    color: 'from-blue-500 to-indigo-600',
    image: 'https://cdn.magicpatterns.com/uploads/goHkoTBNqivNWfS6sUUgPa/Copy_of_Billvast_Shirt_Mock-up_.png',
    stats: '100% Protected',
    delay: 0.4
  }];
  return <Section ref={ref} className="bg-white relative overflow-hidden" data-id="element-298">
      {/* Animated Background Elements */}
      <motion.div style={{
      y,
      opacity
    }} className="absolute top-20 right-10 w-64 h-64 bg-brand-primary/5 rounded-full blur-3xl" data-id="element-299" />
      <motion.div style={{
      y: useTransform(scrollYProgress, [0, 1], [-50, 50]),
      opacity
    }} className="absolute bottom-20 left-10 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" data-id="element-300" />

      <div className="relative z-10" data-id="element-301">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-302">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-primary/10 rounded-full text-brand-primary text-sm font-medium mb-6" data-id="element-303">
            <Sparkles className="w-4 h-4" data-id="element-304" />
            <span data-id="element-305">Our Three Pillars</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-brand-dark mb-4" data-id="element-306">
            How We Keep You <span className="text-brand-primary" data-id="element-307">Safe</span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto" data-id="element-308">
            Three layers of protection that make CampusAparte the most trusted
            student housing platform in Africa.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-309">
          {pillars.map((pillar, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 50
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: pillar.delay
        }} whileHover={{
          y: -10
        }} className="group relative" data-id="element-310">
              <div className="relative h-full bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500" data-id="element-311">
                {/* Gradient Background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${pillar.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} data-id="element-312" />

                {/* Image Section */}
                <div className="relative h-64 overflow-hidden bg-slate-50" data-id="element-313">
                  <motion.img src={pillar.image} alt={pillar.title} className="w-full h-full object-cover" whileHover={{
                scale: 1.1
              }} transition={{
                duration: 0.6
              }} data-id="element-314" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" data-id="element-315" />

                  {/* Floating Icon */}
                  <motion.div initial={{
                scale: 0
              }} whileInView={{
                scale: 1
              }} viewport={{
                once: true
              }} transition={{
                delay: pillar.delay + 0.3,
                type: 'spring'
              }} className={`absolute top-4 right-4 w-12 h-12 rounded-full bg-gradient-to-br ${pillar.color} flex items-center justify-center shadow-lg`} data-id="element-316">
                    <pillar.icon className="w-6 h-6 text-white" data-id="element-317" />
                  </motion.div>

                  {/* Stats Badge */}
                  <motion.div initial={{
                x: -100,
                opacity: 0
              }} whileInView={{
                x: 0,
                opacity: 1
              }} viewport={{
                once: true
              }} transition={{
                delay: pillar.delay + 0.5
              }} className="absolute bottom-4 left-4 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-sm font-bold text-brand-dark" data-id="element-318">
                    {pillar.stats}
                  </motion.div>
                </div>

                {/* Content Section */}
                <div className="p-6" data-id="element-319">
                  <h3 className="text-2xl font-bold text-brand-dark mb-3 group-hover:text-brand-primary transition-colors" data-id="element-320">
                    {pillar.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-321">
                    {pillar.description}
                  </p>

                  {/* Animated Underline */}
                  <motion.div initial={{
                width: 0
              }} whileInView={{
                width: '100%'
              }} viewport={{
                once: true
              }} transition={{
                delay: pillar.delay + 0.7,
                duration: 0.8
              }} className={`h-1 bg-gradient-to-r ${pillar.color} rounded-full mt-6`} data-id="element-322" />
                </div>

                {/* Hover Glow Effect */}
                <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`} data-id="element-323">
                  <div className={`absolute inset-0 bg-gradient-to-br ${pillar.color} opacity-10 blur-xl`} data-id="element-324" />
                </div>
              </div>
            </motion.div>)}
        </div>

        {/* Bottom CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.6
      }} className="mt-16 text-center" data-id="element-325">
          <div className="inline-flex items-center gap-8 px-8 py-4 bg-slate-50 rounded-2xl border border-slate-200" data-id="element-326">
            <div className="flex items-center gap-3" data-id="element-327">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center" data-id="element-328">
                <ShieldCheck className="w-6 h-6 text-emerald-600" data-id="element-329" />
              </div>
              <div className="text-left" data-id="element-330">
                <div className="text-2xl font-bold text-brand-dark" data-id="element-331">850+</div>
                <div className="text-sm text-slate-500" data-id="element-332">Verified Hostels</div>
              </div>
            </div>
            <div className="h-12 w-px bg-slate-300" data-id="element-333" />
            <div className="flex items-center gap-3" data-id="element-334">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center" data-id="element-335">
                <Users className="w-6 h-6 text-brand-primary" data-id="element-336" />
              </div>
              <div className="text-left" data-id="element-337">
                <div className="text-2xl font-bold text-brand-dark" data-id="element-338">5,000+</div>
                <div className="text-sm text-slate-500" data-id="element-339">Happy Students</div>
              </div>
            </div>
            <div className="h-12 w-px bg-slate-300" data-id="element-340" />
            <div className="flex items-center gap-3" data-id="element-341">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-342">
                <Bike className="w-6 h-6 text-blue-600" data-id="element-343" />
              </div>
              <div className="text-left" data-id="element-344">
                <div className="text-2xl font-bold text-brand-dark" data-id="element-345">120+</div>
                <div className="text-sm text-slate-500" data-id="element-346">Ambassadors</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </Section>;
}